import axios from 'axios';

// Get BrowserPrint SDK at call time (not module load) to avoid race conditions
const getBrowserPrint = () => {
  if (!window.BrowserPrint) {
    throw new Error('Zebra BrowserPrint SDK not loaded');
  }
  return window.BrowserPrint;
};

// Gets the default device by type using fetch, then wrap in SDK Device object
const _defaultDevice = async () => {
  try {
    const response = await fetch('http://127.0.0.1:9100/default?type=printer');
    if (!response.ok) {
      throw new Error(`Browser Print responded with ${response.status}`);
    }
    const text = await response.text();
    if (!text || text === '') {
      return null;
    }
    const data = JSON.parse(text);
    const BrowserPrint = getBrowserPrint();
    return new BrowserPrint.Device(data);
  } catch (err) {
    console.info('_defaultDevice error:', err.message);
    throw err;
  }
};

// Discover all available devices using fetch, then wrap in SDK Device objects
const _getAvailableDevices = async () => {
  try {
    const response = await fetch('http://127.0.0.1:9100/available');
    if (!response.ok) {
      throw new Error(`Browser Print responded with ${response.status}`);
    }
    const data = await response.json();
    // Convert raw device objects to SDK Device objects (so send/read methods work)
    const BrowserPrint = getBrowserPrint();
    if (data.printer && Array.isArray(data.printer)) {
      data.printer = data.printer.map(device => new BrowserPrint.Device(device));
    }
    return data;
  } catch (err) {
    console.info('_getAvailableDevices error:', err.message);
    throw err;
  }
};

// Send data to a device (print)
const _print = (device, data = "~wc") => {
  return new Promise((resolve, reject) => {
    // Use SDK device if available (has send method), otherwise error
    const sdkDevice = device._sdkDevice || device;
    if (typeof sdkDevice.send !== 'function') {
      reject(new Error('Invalid device: missing send method. Device may need to be refreshed.'));
      return;
    }
    let settled = false;
    // Zebra SDK send() may not invoke callbacks for ZPL print data — add a timeout
    const timeout = setTimeout(() => {
      if (!settled) {
        settled = true;
        resolve();
      }
    }, 3000);
    sdkDevice.send(data,
      (response) => {
        if (!settled) {
          settled = true;
          clearTimeout(timeout);
          resolve(response);
        }
      },
      (error) => {
        if (!settled) {
          settled = true;
          clearTimeout(timeout);
          console.info('Print error:', error);
          reject(error);
        }
      }
    );
  });
};

// Gets the application configuration
const _getAppConfig = async (device) => {
  const result = await _print(device);
  return result;
};

// ~HS string 2, field r
const PRINT_MODES = { 0: 'Rewind', 1: 'Peel-off', 2: 'Tear-off', 3: 'Cutter', 4: 'Applicator', 5: 'Delayed cut', 6: 'Linerless peel', 7: 'Linerless rewind', 8: 'Partial cutter', 9: 'RFID' };

// Parse Zebra host status response
const _parseHostStatus = (response) => {
  if (!response || typeof response !== 'string') {
    return { isReadyToPrint: false, errors: ['No response from printer'] };
  }

  const errors = [];
  let isReadyToPrint = true;
  const details = {};

  // Zebra ~HS response: three <STX>…<ETX> strings, comma-separated fields.
  // String 1: aaa,b,c,dddd,eee,f,g,h,iii,j,k,l
  //   b=paper out, c=pause, dddd=label length (dots), f=receive buffer full,
  //   k=under temperature, l=over temperature
  // String 2: mmm,n,o,p,q,r,s,t,uuuuuuuu,v,www
  //   o=head up/open, p=ribbon out, q=thermal transfer mode, r=print mode,
  //   t=label waiting (peel-off)
  const lines = response.split('\n').map(l => l.replace(/[^\d,]/g, '')).filter(l => l !== '');
  const line1 = lines.length > 0 ? lines[0].split(',') : [];
  const line2 = lines.length > 1 ? lines[1].split(',') : [];

  if (line1.length >= 6) {
    if (line1[1] === '1') {
      errors.push('Paper out');
      isReadyToPrint = false;
    }
    if (line1[2] === '1') {
      errors.push('Printer paused');
      isReadyToPrint = false;
    }
    const labelLength = parseInt(line1[3], 10);
    if (!isNaN(labelLength)) details.labelLengthDots = labelLength;
    details.bufferFull = line1[5] === '1';
    if (line1[10] === '1') errors.push('Print head under temperature');
    if (line1[11] === '1') {
      errors.push('Print head over temperature');
      isReadyToPrint = false;
    }
  }

  if (line2.length >= 8) {
    if (line2[2] === '1') {
      errors.push('Print head open');
      isReadyToPrint = false;
    }
    details.thermalTransfer = line2[4] === '1';
    // Direct-thermal-only models report ribbon out meaninglessly, so only a
    // printer in thermal transfer mode is blocked by it
    details.ribbonOut = line2[3] === '1';
    if (details.thermalTransfer && details.ribbonOut) {
      errors.push('Ribbon out');
      isReadyToPrint = false;
    }
    details.printMode = PRINT_MODES[line2[5]] || line2[5];
    details.labelWaiting = line2[7] === '1';
  }

  return {
    isReadyToPrint,
    errors: errors.length > 0 ? errors : null,
    status: isReadyToPrint ? 'Ready' : errors.join(', '),
    details,
  };
};

// Send data and read the printer's reply, rejecting after timeoutMs
const _query = (device, data, timeoutMs = 5000) => new Promise((resolve, reject) => {
  const sdkDevice = device._sdkDevice || device;
  if (typeof sdkDevice.send !== 'function' || typeof sdkDevice.read !== 'function') {
    reject(new Error('Device not connected'));
    return;
  }
  const timer = setTimeout(() => reject(new Error('timeout')), timeoutMs);
  sdkDevice.send(data,
    () => {
      sdkDevice.read(
        (response) => { clearTimeout(timer); resolve(response); },
        (error) => { clearTimeout(timer); reject(error); }
      );
    },
    (error) => { clearTimeout(timer); reject(error); }
  );
});

// Read one Link-OS SGD variable. Null when the printer doesn't know it ("?").
const _getVar = async (device, name, timeoutMs) => {
  const response = await _query(device, `! U1 getvar "${name}"\r\n`, timeoutMs);
  const value = String(response ?? '').trim().replace(/^"|"$/g, '').trim();
  return value === '' || value === '?' ? null : value;
};

const _setVar = (device, name, value) =>
  _print(device, `! U1 setvar "${name}" "${value}"\r\n`);

// The settings that decide whether a received label actually marks the media.
// None of them are in our ZPL, so whatever is stored on the printer applies.
const SETTING_VARS = {
  model: 'device.product_name',
  firmware: 'appl.name',
  language: 'device.languages',
  printMethod: 'ezpl.print_method',
  darkness: 'print.tone',
  speed: 'media.speed',
  mediaType: 'ezpl.media_type',
  printWidth: 'ezpl.print_width',
  labelTop: 'zpl.label_top',
  leftPosition: 'zpl.left_position',
  resolutionDpi: 'head.resolution.in_dpi',
};

// Returns { sgd: bool, settings: {...}, hostStatus } — sgd false means the
// printer predates Link-OS and only ~HI / ~HS were available.
const _getPrinterSettings = async (device) => {
  const settings = {};
  let sgd = true;
  // Probe once: an older printer ignores SGD entirely, and waiting out a
  // timeout per variable would take a minute
  try {
    settings.model = await _getVar(device, SETTING_VARS.model, 4000);
  } catch {
    sgd = false;
  }
  if (sgd) {
    for (const [key, name] of Object.entries(SETTING_VARS)) {
      if (key === 'model') continue;
      try {
        settings[key] = await _getVar(device, name, 3000);
      } catch {
        settings[key] = null;
      }
    }
  } else {
    // ~HI host identification — "MODEL,VERSION,DPMM,MEMORY,..."
    try {
      const parts = String(await _query(device, '~HI', 4000)).replace(/[^ -~]/g, '').split(',');
      settings.model = parts[0] || null;
      settings.firmware = parts[1] || null;
    } catch {
      // nothing more to learn
    }
  }
  let hostStatus = null;
  try {
    hostStatus = _parseHostStatus(await _query(device, '~HS', 4000));
  } catch {
    hostStatus = null;
  }
  return { sgd, settings, hostStatus };
};

// The printer's own configuration label — prints with no Evora ZPL involved,
// so a blank one means media or print head, not the label template
const _printConfigLabel = (device) => _print(device, '~WC');

// Media calibration: feeds a few labels to re-learn the gap / label length
const _calibrate = (device) => _print(device, '~JC');

// Border, crosshair, a full-width solid bar (dead head elements show as white
// streaks), text and a barcode, sized to the printer's print width / label length
const _testPatternZpl = ({ width = 600, height = 400, caption = '' }) => {
  const w = Math.max(200, Math.round(width));
  const h = Math.max(150, Math.round(height));
  const font = Math.max(20, Math.round(h / 10));
  const clean = (s) => String(s).replace(/[\^~]/g, ' ');
  return [
    '^XA',
    `^PW${w}`,
    `^LL${h}`,
    '^LH0,0',
    `^FO0,0^GB${w},${h},4^FS`,
    `^FO${Math.round(w / 2)},0^GB1,${h},2^FS`,
    `^FO0,${Math.round(h / 2)}^GB${w},1,2^FS`,
    `^FO10,10^GB${w - 20},${font},${font}^FS`,
    `^FO15,${font + 20}^A0N,${font},${font}^FDEVORA TEST^FS`,
    `^FO15,${font * 2 + 25}^A0N,${Math.round(font * 0.6)},${Math.round(font * 0.6)}^FD${clean(caption)}^FS`,
    `^FO15,${font * 3 + 25}^BY2^BCN,${Math.max(30, Math.round(h / 6))},Y,N,N^FD${w}x${h}^FS`,
    '^PQ1',
    '^XZ',
  ].join('\n');
};

const _getDeviceStatus = async (device, timeoutMs = 5000) => {
  try {
    const sdkDevice = device._sdkDevice || device;
    if (typeof sdkDevice.send !== 'function') {
      return { isReadyToPrint: null, errors: null, status: 'Not connected - refresh devices' };
    }

    // Race the SDK status check against a timeout
    const statusPromise = new Promise((resolve) => {
      sdkDevice.send('~hs',
        () => {
          // Send succeeded, read the response
          sdkDevice.read(
            (response) => {
              const status = _parseHostStatus(response);
              resolve(status);
            },
            (error) => {
              const msg = error?.toString() || '';
              if (msg.includes('Broken pipe') || msg.includes('timeout')) {
                resolve({ isReadyToPrint: null, errors: null, status: 'Status not supported' });
              } else {
                resolve({ isReadyToPrint: null, errors: null, status: 'Unable to read status' });
              }
            }
          );
        },
        (error) => {
          const msg = error?.toString() || '';
          if (msg.includes('Broken pipe')) {
            resolve({ isReadyToPrint: null, errors: null, status: 'Status not supported' });
          } else {
            resolve({ isReadyToPrint: null, errors: null, status: 'Connection error: ' + msg });
          }
        }
      );
    });

    const timeoutPromise = new Promise((resolve) => {
      setTimeout(() => {
        resolve({ isReadyToPrint: null, errors: null, status: 'Printer did not respond (timeout)' });
      }, timeoutMs);
    });

    return await Promise.race([statusPromise, timeoutPromise]);
  } catch (error) {
    return { isReadyToPrint: null, errors: [error.message], status: 'Error: ' + error.message };
  }
};

// Query the printer's physical head resolution. Tries the Link-OS SGD variable
// first, then falls back to ~HI host identification for older firmware.
// Returns { dpi, dpmm } or null when the printer doesn't answer either query.
const _getPrinterResolution = async (device, timeoutMs = 5000) => {
  const sdkDevice = device._sdkDevice || device;
  if (typeof sdkDevice.send !== 'function' || typeof sdkDevice.read !== 'function') {
    return null;
  }

  const query = (data) => _query(device, data, timeoutMs);

  const DPI_TO_DPMM = { 152: 6, 203: 8, 300: 12, 600: 24 };

  // Link-OS SGD query — responds with the dpi in quotes, e.g. "300"
  try {
    const response = await query('! U1 getvar "head.resolution.in_dpi"\r\n');
    const dpi = parseInt(String(response).replace(/[^\d]/g, ''), 10);
    if (DPI_TO_DPMM[dpi] != null) {
      return { dpi: dpi, dpmm: DPI_TO_DPMM[dpi] };
    }
  } catch (err) {
    console.info('SGD resolution query failed, trying ~HI:', err?.message || err);
  }

  // ~HI host identification — "MODEL,VERSION,DPMM,MEMORY,..." e.g. ZD421-203dpi,V84.20.18Z,8,8192KB
  try {
    const response = await query('~HI');
    const dpmm = parseInt(String(response).split(',')[2], 10);
    const entry = Object.entries(DPI_TO_DPMM).find(([, mm]) => mm === dpmm);
    if (entry != null) {
      return { dpi: parseInt(entry[0], 10), dpmm: dpmm };
    }
  } catch (err) {
    console.info('~HI resolution query failed:', err?.message || err);
  }

  return null;
};

const _getDevices = async () => {
  try {
    const defaultDevice = await _defaultDevice().catch(() => null);
    const deviceList = await _getAvailableDevices().catch(() => null);
    const printers = await axios.get('/api/devices');

    let devices = { registered: [], new: [], notConnected: [], hasRegisteredDevices: false, default: defaultDevice };

    if (deviceList != null && deviceList !== '' &&
        deviceList.printer != null && deviceList.printer.length > 0) {
      if (printers.data != null && printers.data !== '' && printers.data.length > 0) {
        deviceList.printer.forEach(sdkDevice => {
          if (printers.data.some(printer => printer.name === sdkDevice.name && printer.uid === sdkDevice.uid)) {
            let printer = printers.data.find(printer => printer.name === sdkDevice.name && printer.uid === sdkDevice.uid);
            // Merge backend data with SDK device, preserving SDK device reference for printing
            const mergedDevice = {
              ...printer,
              _sdkDevice: sdkDevice  // Keep SDK device reference for send/read methods
            };
            devices.registered.push(mergedDevice);
            devices.hasRegisteredDevices = true;
            if (devices.default) {
              if (printer.name === devices.default.name && printer.uid === devices.default.uid) {
                devices.default = mergedDevice;
              }
            }
          } else {
            // New device - keep the SDK device as-is (already has send/read methods)
            devices.new.push(sdkDevice);
          }
          devices.hasDevices = true;
        });
        printers.data.forEach(printer => {
          if (!deviceList.printer.some(device => device.name === printer.name && device.uid === printer.uid))
            devices.notConnected.push(printer);
        });
      } else {
        devices.new = deviceList.printer;
        devices.hasRegisteredDevices = false;
        devices.hasDevices = true;
      }
    } else {
      devices.notConnected = printers.data || [];
    }
    return devices;
  } catch (err) {
    console.error('getDevices error:', err);
    return err;
  }
};

const _testPrint = async (printer) => {
  try {
    const serial = 'evoraTechnologies.com';
    const zpl = `^XA
    ^BY2,2,100
    ^FO20,20^BC^FD${serial}^FS
    ^XZ`;
    const result = await _print(printer, zpl);
    return result;
  } catch (err) {
    return { error: true, message: err.message };
  }
};

export const DeviceHelper = {
  getAppConfig: _getAppConfig,
  getDevices: _getDevices,
  getDeviceStatus: _getDeviceStatus,
  getPrinterResolution: _getPrinterResolution,
  getPrinterSettings: _getPrinterSettings,
  setVar: _setVar,
  printConfigLabel: _printConfigLabel,
  calibrate: _calibrate,
  testPatternZpl: _testPatternZpl,
  print: _print,
  testPrint: _testPrint
};
